import React, { useEffect, useState } from "react";
import {
  Form,
  InputNumber,
  Button,
  Card,
  Tabs,
  message,
  Typography,
  Grid,
  Select,
} from "antd";
import { PlusOutlined, DeleteOutlined, SaveOutlined } from "@ant-design/icons";
import * as settingsService from "../../services/settingsService";
import Loadingpage from "../Loadingpage/Loadingpage";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const Pointssection = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [activeRole, setActiveRole] = useState("global"); 
  const [initialRoleValues, setInitialRoleValues] = useState({});
  const [changedRoles, setChangedRoles] = useState({});
  const screens = useBreakpoint();
  const upgradeRoles = ["vip", "vvip"];

  const normalizeRoleValues = (values = {}) => {
    const normalized = { ...values };
    Object.keys(normalized).forEach((key) => {
      if (Array.isArray(normalized[key])) {
        normalized[key] = normalized[key].filter(Boolean);
      } else if (normalized[key] !== undefined && normalized[key] !== null) {
        normalized[key] = Number(normalized[key]);
      }
    });
    return normalized;
  };

  const updateInitialValues = (role, values) => {
    const normalizedValues = normalizeRoleValues(values);
    setInitialRoleValues((prev) => ({
      ...prev,
      [role]: normalizedValues,
    }));
    setChangedRoles((prev) => ({
      ...prev,
      [role]: false,
    }));
    return normalizedValues;
  };

  const hasRoleChanged = (roleName, currentValues) => {
    return (
      JSON.stringify(normalizeRoleValues(currentValues)) !==
      JSON.stringify(initialRoleValues[roleName] || {})
    );
  };

  // 1. Fetching Logic
  const fetchSettings = async (rank) => {
    setLoading(true);
    try {
      const resp = await settingsService.getSecretPointSettings(rank);
      if (resp.success) {
        if (rank) {
          const s = resp.result;
          if (rank === "global") {
            const globalValues = updateInitialValues("global", {
              vipThreshold: s.roleUpgrades?.vipThreshold || 100,
              vvipThreshold: s.roleUpgrades?.vvipThreshold || 500,
            });
            form.setFieldsValue({
              global: globalValues,
            });
          } else if (s) {
            const rankValues = updateInitialValues(rank, {
              win_points: s.win?.point || 0,
              win_streak_round: s.winStreak?.round || 0,
              win_streak_points: s.winStreak?.point || 0,
              loss_points: s.loss?.point || 0,
              loss_streak_round: s.lossStreak?.round || 0,
              loss_streak_points: s.lossStreak?.point || 0,
              streak_combo_rounds:
                s.hitCombos?.[0]?.pattern
                  ?.split("")
                  .map((char) => (char === "W" ? "win" : "loss")) || [],
              streak_combo_points: s.hitCombos?.[0]?.point || 0,
              daily_login_count: s.dailyLogin?.count || 1,
              daily_login_points: s.dailyLogin?.point || 0,
              consecutive_login_count: s.consecutiveLogin?.days || 0,
              consecutive_login_points: s.consecutiveLogin?.point || 0,
              originalSecretPoint: s.originalSecretPoint || 0,
              deposit_points: s.deposit?.point || 0,
            });
            form.setFieldsValue({
              [rank]: rankValues,
            });
          }
        } else {
          const { rankSettings, globalSettings } = resp.result;

          const formData = { global: globalSettings };
          rankSettings.forEach((s) => {
            formData[s.rank] = {
              win_points: s.win?.point || 0,
              win_streak_round: s.winStreak?.round || 0,
              win_streak_points: s.winStreak?.point || 0,
              loss_points: s.loss?.point || 0,
              loss_streak_round: s.lossStreak?.round || 0,
              loss_streak_points: s.lossStreak?.point || 0,
              streak_combo_rounds:
                s.hitCombos?.[0]?.pattern
                  ?.split("")
                  .map((char) => (char === "W" ? "win" : "loss")) || [],
              streak_combo_points: s.hitCombos?.[0]?.point || 0,
              daily_login_count: s.dailyLogin?.count || 1,
              daily_login_points: s.dailyLogin?.point || 0,
              consecutive_login_count: s.consecutiveLogin?.days || 0,
              consecutive_login_points: s.consecutiveLogin?.point || 0,
              originalSecretPoint: s.originalSecretPoint || 0,
              deposit_points: s.deposit?.point || 0,
            };
          });

          Object.entries(formData).forEach(([role, values]) => {
            formData[role] = updateInitialValues(role, values);
          });
          form.setFieldsValue(formData);
        }
      } else {
        messageApi.error(resp.message || "Failed to load settings");
      }
    } catch (error) {
      console.error("Fetch Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings(activeRole);
  }, [activeRole]);

  // 2. Saving Logic
  const onFinish = async (roleName) => {
    setLoading(true);
    try {
        let payload;
        if (roleName === 'global') {
            const values = form.getFieldValue('global');
            payload = {
                rank: 'global',
                ...values
            };
        } else {
            const data = form.getFieldValue(roleName);
            payload = {
                rank: roleName,
                originalSecretPoint: data.originalSecretPoint || 0,
                win: { point: data.win_points },
                winStreak: { round: data.win_streak_round, point: data.win_streak_points },
                loss: { point: data.loss_points },
                lossStreak: { round: data.loss_streak_round, point: data.loss_streak_points },
                dailyLogin: { point: data.daily_login_points },
                consecutiveLogin: { days: data.consecutive_login_count, point: data.consecutive_login_points },
                deposit: { point: data.deposit_points || 1 },
                hitCombos: data.streak_combo_rounds?.length > 0 ? [{
                    pattern: data.streak_combo_rounds.map(r => r === 'win' ? 'W' : 'L').join(''),
                    point: data.streak_combo_points
                }] : []
            };
        }

        const resp = await settingsService.updateSecretPointSettings(payload);
        if (resp.success) {
            messageApi.success(`${roleName.toUpperCase()} settings saved successfully`);
            fetchSettings(roleName);
        } else {
            messageApi.error(resp.message || "Update failed");
        }
    } catch (error) {
        console.error("Save Error:", error);
        messageApi.error("An unexpected error occurred");
    } finally {
        setLoading(false);
    }
  };

  /**
   * GLOBAL SETTINGS FORM
   */
  const renderGlobalSettingsForm = () => (
    <Card className="card-bg-clr" style={{ marginTop: "20px", border: "none", background: "rgba(255, 255, 255, 0.03)", borderRadius: "12px" }}>
        <Title level={4} style={{ color: "#fff", marginBottom: "24px" }}>Master Upgrade Thresholds</Title>
        <div style={{ display: "grid", gridTemplateColumns: screens.md ? "1fr 1fr" : "1fr", gap: "20px" }}>
            <Form.Item label={<span style={{ color: "#fff" }}>VIP Upgrade Threshold (Total Deposit)</span>} name={["global", "vipThreshold"]}>
                <InputNumber style={{ width: "100%" }} placeholder="100" min={0} />
            </Form.Item>
            <Form.Item label={<span style={{ color: "#fff" }}>VVIP Upgrade Threshold (Total Deposit)</span>} name={["global", "vvipThreshold"]}>
                <InputNumber style={{ width: "100%" }} placeholder="500" min={0} />
            </Form.Item>
        </div>
        <div style={{ marginTop: "24px", display: "flex", justifyContent: "flex-end" }}>
            <Button type="primary" icon={<SaveOutlined />} onClick={() => onFinish('global')} loading={loading} disabled={!changedRoles.global || loading} style={{ height: "42px", padding: "0 24px", borderRadius: "6px", background: changedRoles.global ? "linear-gradient(90deg, #FC4420 0%, #d9361b 100%)" : "rgba(255,255,255,0.16)", border: "none" }}>
                Save Global Thresholds
            </Button>
        </div>
    </Card>
  );

  const renderRoleSettingsForm = (roleName) => (
    <Card
      className="card-bg-clr"
      style={{
        marginTop: "20px",
        border: "none",
        background: "rgba(255, 255, 255, 0.03)",
        borderRadius: "12px",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: screens.md ? "1fr 1fr 1fr" : "1fr",
          gap: "20px",
        }}
      >
        <Form.Item
          label={<span style={{ color: "#fff" }}>Win Secret Points</span>}
          name={[roleName, "originalSecretPoint"]}
        >
          <InputNumber style={{ width: "100%" }} placeholder="0" min={0} />
        </Form.Item>
        <Form.Item
          label={<span style={{ color: "#fff" }}>Win Points</span>}
          name={[roleName, "win_points"]}
          rules={[{ required: true, message: "Please enter win points" }]}
        >
          <InputNumber style={{ width: "100%" }} placeholder="10" min={0} />
        </Form.Item>

        <Form.Item
          label={<span style={{ color: "#fff" }}>Win Streak Round</span>}
          name={[roleName, "win_streak_round"]}
          rules={[{ required: true, message: "Please enter win streak round" }]}
        >
          <InputNumber style={{ width: "100%" }} placeholder="3" min={0} />
        </Form.Item>

        <Form.Item
          label={<span style={{ color: "#fff" }}>Win Streak Points</span>}
          name={[roleName, "win_streak_points"]}
          rules={[
            { required: true, message: "Please enter win streak points" },
          ]}
        >
          <InputNumber style={{ width: "100%" }} placeholder="20" min={0} />
        </Form.Item>

        <Form.Item
          label={<span style={{ color: "#fff" }}>Loss Points</span>}
          name={[roleName, "loss_points"]}
          rules={[{ required: true, message: "Please enter loss points" }]}
        >
          <InputNumber style={{ width: "100%" }} placeholder="5" min={0} />
        </Form.Item>

        <Form.Item
          label={<span style={{ color: "#fff" }}>Loss Streak Round</span>}
          name={[roleName, "loss_streak_round"]}
          rules={[
            { required: true, message: "Please enter loss streak round" },
          ]}
        >
          <InputNumber style={{ width: "100%" }} placeholder="3" min={0} />
        </Form.Item>

        <Form.Item
          label={<span style={{ color: "#fff" }}>Loss Streak Points</span>}
          name={[roleName, "loss_streak_points"]}
          rules={[
            { required: true, message: "Please enter loss streak points" },
          ]}
        >
          <InputNumber style={{ width: "100%" }} placeholder="10" min={0} />
        </Form.Item>
        <Form.Item
            label={<span style={{ color: "#fff" }}>Points Per Deposit</span>}
            name={[roleName, "deposit_points"]}
        >
            <InputNumber style={{ width: "100%" }} placeholder="10" min={0} />
        </Form.Item>
      </div>

      <div
        style={{
          marginTop: "32px",
          display: "grid",
          gridTemplateColumns: screens.lg ? "1fr 1fr" : "1fr",
          gap: "30px",
        }}
      >
        <div>
          <Title level={5} style={{ color: "#fff", marginBottom: "14px" }}>
            Streak Combo
          </Title>
          <Form.List name={[roleName, "streak_combo_rounds"]}>
            {(fields, { add, remove }) => (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
                {fields.map(({ key, name, ...restField }) => (
                  <div
                    key={key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "6px 8px",
                      borderRadius: "8px",
                      border: "1px solid rgba(255,255,255,0.12)",
                    }}
                  >
                    <Form.Item
                      {...restField}
                      name={name}
                      noStyle
                      rules={[{ required: true, message: "" }]}
                    >
                      <Select
                        placeholder="W/L"
                        style={{ width: "72px" }}
                        options={[
                          { value: "win", label: "W" },
                          { value: "loss", label: "L" },
                        ]}
                        className={(form.getFieldValue([roleName, "streak_combo_rounds", name]) === "win")
                          ? "streak-combo-select streak-combo-win"
                          : (form.getFieldValue([roleName, "streak_combo_rounds", name]) === "loss")
                            ? "streak-combo-select streak-combo-loss"
                            : "streak-combo-select"}
                      />
                    </Form.Item>
                    <DeleteOutlined
                      onClick={() => remove(name)}
                      style={{
                        color: "#ff4d4f",
                        cursor: "pointer",
                        marginLeft: "4px",
                        fontSize: "12px",
                      }}
                    />
                  </div>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add()}
                  icon={<PlusOutlined />}
                  style={{
                    background: "transparent",
                    color: "#FC4420",
                    borderColor: "#FC4420",
                    borderRadius: "8px",
                    height: "38px",
                  }}
                >
                  Add Combo
                </Button>
              </div>
            )}
          </Form.List>
        </div>

        <Form.Item
          label={<span style={{ color: "#fff" }}>Streak Combo Points</span>}
          name={[roleName, "streak_combo_points"]}
          rules={[
            { required: true, message: "Please enter streak combo points" },
          ]}
        >
          <InputNumber style={{ width: "100%" }} placeholder="50" min={0} />
        </Form.Item>
      </div>

      <div
        style={{
          marginTop: "32px",
          display: "grid",
          gridTemplateColumns: screens.md ? "1fr 1fr" : "1fr",
          gap: "20px",
        }}
      >
        <Form.Item
          label={<span style={{ color: "#fff" }}>Daily Login Count</span>}
          name={[roleName, "daily_login_count"]}
          rules={[
            { required: true, message: "Please enter daily login count" },
          ]}
        >
          <InputNumber style={{ width: "100%" }} placeholder="1" min={0} />
        </Form.Item>
        <Form.Item
          label={<span style={{ color: "#fff" }}>Daily Login Points</span>}
          name={[roleName, "daily_login_points"]}
          rules={[
            { required: true, message: "Please enter daily login points" },
          ]}
        >
          <InputNumber style={{ width: "100%" }} placeholder="5" min={0} />
        </Form.Item>
        <Form.Item
          label={<span style={{ color: "#fff" }}>Consecutive Login Count</span>}
          name={[roleName, "consecutive_login_count"]}
          rules={[
            { required: true, message: "Please enter consecutive login count" },
          ]}
        >
          <InputNumber style={{ width: "100%" }} placeholder="1" min={0} />
        </Form.Item>
        <Form.Item
          label={<span style={{ color: "#fff" }}>Consecutive Login Points</span>}
          name={[roleName, "consecutive_login_points"]}
          rules={[
            { required: true, message: "Please enter consecutive login points" },
          ]}
        >
          <InputNumber style={{ width: "100%" }} placeholder="5" min={0} />
        </Form.Item>
      </div>

      <div
        style={{
          marginTop: "24px",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={() => {
            form
              .validateFields([roleName])
              .then((values) => {
                onFinish(roleName);
              })
              .catch(() => {
                messageApi.warning("Please fill all required fields");
              });
          }}
          loading={loading}
          disabled={!changedRoles[roleName] || loading}
          style={{
            height: "42px",
            padding: "0 24px",
            borderRadius: "6px",
            background: changedRoles[roleName] ? "linear-gradient(90deg, #FC4420 0%, #d9361b 100%)" : "rgba(255,255,255,0.16)",
            border: "none",
          }}
        >
          Save {roleName.toUpperCase()} Settings
        </Button>
      </div>
    </Card>
  );

  const tabItems = [
    {
        key: "global",
        label: <span style={{ padding: "0 12px" }}>Global Settings</span>,
        children: renderGlobalSettingsForm(),
    },
    {
      key: "basic",
      label: <span style={{ padding: "0 12px" }}>Basic Role</span>,
      children: renderRoleSettingsForm("basic"),
    },
    {
      key: "vip",
      label: <span style={{ padding: "0 12px" }}>VIP Role</span>,
      children: renderRoleSettingsForm("vip"),
    },
    {
      key: "vvip",
      label: <span style={{ padding: "0 12px" }}>VVIP Role</span>,
      children: renderRoleSettingsForm("vvip"),
    },
  ];

  return (
    <div style={{ padding: "24px", minHeight: "100vh", background: "#000" }}>
      <Loadingpage isLoading={loading} />
      {contextHolder}
      <style>
        {`
          .streak-combo-select .ant-select-selector {
            border-radius: 8px !important;
            border: none !important;
            color: #fff !important;
            font-weight: 700;
            text-align: center;
          }
          .streak-combo-win .ant-select-selector {
            background: #1f7a1f !important;
          }
          .streak-combo-loss .ant-select-selector {
            background: #a61d24 !important;
          }
          .streak-combo-select .ant-select-selection-item {
            color: #fff !important;
            text-align: center;
          }
          .streak-combo-select .ant-select-arrow {
            color: #fff !important;
          }
        `}
      </style>
      <div style={{ marginBottom: "32px" }}>
        <Title level={2} style={{ color: "#fff", margin: 0, fontSize: "28px" }}>
          Points Settings
        </Title>
        <Text style={{ color: "rgba(255,255,255,0.45)", fontSize: "16px" }}>
          Configure trading rules and limits for user roles
        </Text>
      </div>

      <Form
        form={form}
        layout="vertical"
        onValuesChange={(changedValues, allValues) => {
          const roleName = Object.keys(changedValues)[0];
          setChangedRoles((prev) => ({
            ...prev,
            [roleName]: hasRoleChanged(roleName, allValues[roleName]),
          }));
        }}
        autoComplete="off"
        requiredMark={false}
      >
        <Tabs
          activeKey={activeRole}
          onChange={setActiveRole}
          items={tabItems}
          className="custom-tabs"
          style={{ color: "#fff" }}
        />
      </Form>
    </div>
  );
};

export default Pointssection;
